import {forwardRef, memo} from 'react';
import {FixedSizeList} from 'react-window';
import getImageAltText from 'src/utils/getImageAltText';
import formatPlaceType from 'src/utils/formatPlaceType';
import getImageUrl from 'src/utils/getImageUrl';
import {ratingStar} from 'src/utils/icons';
import FadingImage from '../FadingImage';
import BaseButton from '../BaseButton';
import RemixIcon from '../RemixIcon';

const PADDING_SIZE = 12;
const ITEM_SIZE = 64;

export type PlacesItemData = {
  places: Place[];
  reviewsByPlaceId: {[placeId: string]: Review | undefined};
  setSelectedPlace: (place: Partial<Place>, options: {forceFlyTo?: boolean}) => void;
};

const PlaceItem = memo(({style, index, data}: {style: any; index: number; data: PlacesItemData}) => {
  const place = data.places[index];
  const review = data.reviewsByPlaceId[place._id];

  const firstImage = place.images?.[0];
  const imageUrl = firstImage ? getImageUrl(firstImage.path, 'thumb') : '';

  return (
    <BaseButton
      onPress={() => data.setSelectedPlace(place, {forceFlyTo: true})}
      style={{...style, top: style.top + PADDING_SIZE}}
      className="group h-[64px] flex items-center justify-between py-0 px-2 rounded-2xl duration-0 hover:bg-(--button-bg-hover) data-[pressed]:bg-(--button-bg-active)"
    >
      <div className="h-12 w-12 mr-4 shrink-0 bg-(--secondary-color) nice-image rounded-xl after:rounded-xl">
        {firstImage ? (
          <FadingImage
            src={imageUrl}
            fill
            className="object-cover"
            alt={getImageAltText(firstImage, place.name, place.countryName)}
          />
        ) : null}
      </div>

      <div className="flex flex-col grow items-start w-0">
        <div className="flex items-center gap-[2px] w-full">
          <span className="text-lg font-semibold truncate leading-snug">{place.name}</span>
        </div>
        <span className="text-sm text-(--gray-text) text-left truncate w-full">
          {formatPlaceType(place.placeType)} in {place.countryName}
        </span>
      </div>

      {review ? (
        <div className="flex bg-(--green-secondary) p-1 text-(--green-primary) rounded-md gap-[2px] items-center mr-1 ml-1">
          {review.mainRating}
          <RemixIcon icon={ratingStar} size={11} color="var(--green-primary)" />
        </div>
      ) : null}
    </BaseButton>
  );
});

const innerElementType = forwardRef<HTMLDivElement>(({style, ...rest}: any, ref) => (
  <div ref={ref} style={{...style, height: style.height + PADDING_SIZE * 2}} {...rest} />
));

const itemKey = (index: number, data: PlacesItemData) => data.places[index]._id;

type Props = {
  numberOfItemsToRender: number;
  virtualScroll: boolean;
  containerHeight: number;
  itemData: PlacesItemData;
};

const MapPlacesList = ({numberOfItemsToRender, virtualScroll, containerHeight, itemData}: Props) => {
  return (
    <FixedSizeList
      height={virtualScroll ? containerHeight : numberOfItemsToRender * ITEM_SIZE + 2 * PADDING_SIZE}
      itemCount={numberOfItemsToRender}
      itemSize={64}
      itemData={itemData}
      innerElementType={innerElementType}
      width="100%"
      itemKey={itemKey}
    >
      {PlaceItem}
    </FixedSizeList>
  );
};

export default MapPlacesList;
